// Spec §6's 120 × 24 sparkline: a quantity over its course on its own range, the current time marked
// by a 1 px playhead. The inspector's sim fields and Drugs' Levels use it.

import { useMemo } from 'react';

const W = 120;
const H = 24;

/**
 * The polyline's points. A course longer than one sample a half pixel draws each half pixel's lowest and
 * highest sample, in order, so a 46,001-frame soma trace draws as cheaply as a cycle and keeps its spikes.
 */
export function points(y: ArrayLike<number>): string {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < y.length; i++) {
    const v = y[i]!;
    if (!Number.isFinite(v)) continue;
    lo = Math.min(lo, v);
    hi = Math.max(hi, v);
  }
  if (!Number.isFinite(lo)) return '';
  const span = hi - lo > 1e-9 ? hi - lo : 1;
  const last = Math.max(1, y.length - 1);
  const out: string[] = [];
  const at = (i: number) => out.push(`${((i / last) * W).toFixed(1)},${(H - 2 - ((y[i]! - lo) / span) * (H - 4)).toFixed(1)}`);
  if (y.length <= 2 * W + 1) {
    for (let i = 0; i < y.length; i++) if (Number.isFinite(y[i]!)) at(i);
    return out.join(' ');
  }
  const buckets = 2 * W;
  for (let b = 0; b < buckets; b++) {
    const end = Math.floor(((b + 1) * y.length) / buckets);
    let min = -1;
    let max = -1;
    for (let i = Math.floor((b * y.length) / buckets); i < end; i++) {
      const v = y[i]!;
      if (!Number.isFinite(v)) continue;
      if (min < 0 || v < y[min]!) min = i;
      if (max < 0 || v > y[max]!) max = i;
    }
    if (min < 0) continue;
    at(Math.min(min, max));
    if (max !== min) at(Math.max(min, max));
  }
  return out.join(' ');
}

/** `at` is where the playhead sits, 0 to 1 along the course; null for none. */
export function Sparkline({ y, at }: { y: ArrayLike<number>; at: number | null }) {
  const pts = useMemo(() => points(y), [y]);
  return (
    <svg className="spark" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline points={pts} />
      {at !== null ? <line x1={at * W} x2={at * W} y1={0} y2={H} /> : null}
    </svg>
  );
}
